import OriginalPhoneField, {
    PhoneInputProps,
    PhoneInputRefType,
} from "@linhnguyen96114/react-native-phone-input";
import parsePhoneNumber from "libphonenumber-js";
import React, {
    useCallback,
    useEffect,
    useId,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { StyleSheet, Platform } from "react-native";
import BaseField, { BaseFieldProps } from "./BaseField";
import { useTheme } from "../hooks/use-theme";
import DoneAccessoryBar from "./DoneAccessoryBar";

type PhoneFieldProps = Omit<BaseFieldProps, "component"> &
    PhoneInputProps & {
        onPhoneNumberChanged?: (number: string) => void;
        onBlur?: () => Promise<void> | void;
        onSubmitEditing?: () => void;
        isValid?: boolean;
        withIosDoneButton?: boolean;
    };

export type PhoneFieldHandle = {
    isValidNumber: (num: string) => boolean;
    formatPhoneNumber: () => void;
};

/** Country pre-selected in the picker, and the one national numbers are parsed against. */
export const DEFAULT_PHONE_COUNTRY = "CH";

/**
 * Parses a value handled by this field. The field emits an international number (or an empty
 * string once cleared), so the result tells an empty field from an incomplete one and yields the
 * E.164 form backends expect, national trunk prefix dropped when the country requires it
 * ("+41 079 …" → "+4179…"). Values stored before a screen used this component may carry no prefix
 * at all, hence the fallback on the default country.
 */
export const parsePhoneFieldValue = (value?: string | null) =>
    parsePhoneNumber(value || "", DEFAULT_PHONE_COUNTRY);

const PhoneField = ({
    ref: forwardedRef,
    onPhoneNumberChanged,
    style,
    isValid = true,
    disabled,
    label,
    placeholder,
    value,
    error,
    onFocus: propsOnFocus,
    onBlur: propsOnBlur,
    ...props
}: PhoneFieldProps & { ref?: React.Ref<PhoneFieldHandle> }) => {
    const uniqueId = useId();
    const [isFocused, setIsFocused] = useState(false);
    const styles = useStyles();
    const phoneInputRef = useRef<PhoneInputRefType>(null);
    const isValidNumber = (arg: string): boolean => {
        return !!phoneInputRef.current?.isValidNumber(arg);
    };

    const formatPhoneNumber = useCallback(() => {
        const inputValue =
            phoneInputRef.current
                ?.getNumberAfterPossiblyEliminatingZero()
                .formattedNumber?.replaceAll(" ", "") || "";

        return inputValue;
    }, []);

    useImperativeHandle(forwardedRef, () => ({ isValidNumber, formatPhoneNumber }));

    const parsedValue = useMemo(() => parsePhoneFieldValue(value), [value]);

    const onFocus = useCallback(() => {
        setIsFocused(true);
        propsOnFocus?.();
    }, [propsOnFocus]);

    const onBlur = useCallback(() => {
        setIsFocused(false);
        propsOnBlur?.();
    }, [propsOnBlur]);

    const fieldId = useMemo(() => uuidv4(), []);

    // autoFocus prop doesn't natively work on react-native-phone-number-input on web
    useEffect(() => {
        if (Platform.OS === "web" && props.autoFocus) {
            document.getElementById(fieldId)?.focus?.();
        }

        // Only run on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { onSubmitEditing, ...phoneInputProps } = props;
    return (
        <>
            <BaseField
                style={style}
                labelLeftPadding={130}
                value={!!value}
                disabled={disabled}
                label={label}
                placeholder={placeholder}
                onFocus={onFocus}
                onBlur={onBlur}
                isFocused={isFocused}
                error={error}
                componentRef={phoneInputRef}
                component={
                    // This component OriginalPhoneField is Legacy, we should replace it as soon as possible (latest update in 2021).
                    <OriginalPhoneField
                        defaultValue={parsedValue?.nationalNumber}
                        defaultCode={parsedValue?.country || DEFAULT_PHONE_COUNTRY}
                        layout="first"
                        onChangeFormattedText={(newValue) => {
                            onPhoneNumberChanged?.(newValue);
                        }}
                        textContainerStyle={styles.textContainer}
                        textInputStyle={[
                            { padding: 0, paddingLeft: Platform.OS === "web" ? 12 : 0 },
                            disabled && styles.textInputDisabled,
                        ]}
                        codeTextStyle={[styles.codeText, disabled && styles.textInputDisabled]}
                        flagButtonStyle={{ width: "auto" }}
                        countryPickerButtonStyle={{
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                        placeholder=" "
                        containerStyle={[styles.container]}
                        countryPickerProps={{
                            // explicitly specify this to avoid error when disabled in Scrollview.
                            // @see https://github.com/garganurag893/react-native-phone-number-input/issues/117
                            disableNativeModal: false,
                        }}
                        textInputProps={{
                            id: fieldId,
                            //eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            //@ts-ignore
                            selectionColor: null,
                            onSubmitEditing,
                            keyboardType: "number-pad",
                            keyboardAppearance: "light",
                            returnKeyType: isValid ? "done" : undefined,
                            onFocus,
                            onBlur,
                            inputAccessoryViewID: Platform.OS === "ios" ? uniqueId : undefined,
                        }}
                        disabled={disabled}
                        {...phoneInputProps}
                    />
                }
            />
            <DoneAccessoryBar nativeId={uniqueId} />
        </>
    );
};

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                container: {
                    alignItems: "center",
                    backgroundColor: "transparent",
                    width: "100%",
                    height: Platform.OS === "web" ? 20 : 28,
                    padding: 0,
                },
                textContainer: {
                    backgroundColor: "transparent",
                    paddingVertical: 0,
                    paddingHorizontal: 0,
                },
                textInputDisabled: {
                    color: colors.backdrop,
                },
                codeText: {
                    width: 40,
                    ...fonts.regular,
                    marginRight: 2,
                },
            }),
        [colors.backdrop, fonts.regular],
    );
};

export default PhoneField;
